import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import FloatingNav from '../components/FloatingNav';
import BackToHome from '../components/BackToHome';
import { ASSETS } from '../config/assets';
import { Star, MessageCircle, Share2, TrendingUp, CheckCircle } from 'lucide-react';

const PositiveReviewsPage = () => {
  const { t } = useLanguage();

  const steps = [
    {
      icon: MessageCircle,
      title: t('positiveReviews.step1Title'),
      desc: t('positiveReviews.step1Desc'),
    },
    {
      icon: Share2,
      title: t('positiveReviews.step2Title'),
      desc: t('positiveReviews.step2Desc'),
    },
    {
      icon: TrendingUp,
      title: t('positiveReviews.step3Title'),
      desc: t('positiveReviews.step3Desc'),
    },
  ];

  const benefits = [
    t('positiveReviews.benefit1'),
    t('positiveReviews.benefit2'),
    t('positiveReviews.benefit3'),
    t('positiveReviews.benefit4'),
  ];

  return (
    <div data-testid="positive-reviews-page" className="bg-[#0D0D0D]">
      {/* Floating Navigation */}
      <FloatingNav sections={[]} />
      <BackToHome />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center bg-gradient-to-b from-growthgreen/20 to-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Star className="w-8 h-8 text-green-400" fill="currentColor" strokeWidth={1.5} />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-green-400">
                  {t('positiveReviews.title')}
                </span>
              </div>
              <h1 
                data-testid="positive-reviews-title"
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6"
              >
                {t('positiveReviews.subtitle')}
              </h1>
              <p className="text-lg sm:text-xl text-white/60 leading-relaxed mb-8">
                {t('positiveReviews.heroDesc')}
              </p>
              <Link to="/contact">
                <Button 
                  size="lg"
                  data-testid="positive-reviews-cta"
                  className="btn-demo-cta text-white rounded-lg px-8 py-6"
                >
                  {t('nav.requestDemo')}
                </Button>
              </Link>
            </div>
            <div className="hidden lg:flex justify-start pl-8">>
              <img 
                src="https://customer-assets.emergentagent.com/job_tap-platform-build/artifacts/nz27jntc_FF51F22A-AD46-48EC-8710-C09ED718BDCD.png"
                alt="Fairfax Lounge Review"
                className="w-[420px] h-auto rounded-3xl shadow-2xl shadow-black/50"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 lg:py-28 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white">
              {t('positiveReviews.howItWorks')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {steps.map((step, idx) => (
              <div
                key={idx}
                data-testid={`reviews-step-${idx}`}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:-translate-y-1 hover:border-green-500/30 transition-all duration-300 relative"
              >
                <div className="absolute -top-4 -left-4 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                  {idx + 1}
                </div>
                <div className="p-3 bg-green-500/10 rounded-xl w-fit mb-6">
                  <step.icon className="w-6 h-6 text-green-400" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-medium text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-white/50 leading-relaxed">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 lg:py-28 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-8">
                {t('positiveReviews.benefitsTitle')}
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span className="text-white/80 text-lg">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img
                src={ASSETS.DELFRISCOS_PHONE}
                alt="Guest leaving review on phone"
                className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PositiveReviewsPage;
